/**
 * Shared work-profile maxes (Stunden/Woche, Tage/Woche, Stunden/Arbeitstag).
 * Product defaults live in `app_settings.work_profile_limits`; these constants are
 * the absolute ceilings and the fallback when settings are unavailable.
 *
 * Coupling rule: weeklyHours ≤ workdaysPerWeek × maxHoursPerWorkday
 * (and also ≤ maxWeeklyHours).
 */

export const WORK_PROFILE_LIMITS_DEFAULT = {
  maxWeeklyHours: 48,
  maxWorkdaysPerWeek: 5,
  maxHoursPerWorkday: 10,
} as const;

/** Hard ceilings — DB CHECK / settings schema must not exceed these. */
export const WORK_PROFILE_LIMITS_ABSOLUTE = {
  maxWeeklyHours: 80,
  maxWorkdaysPerWeek: 7,
  maxHoursPerWorkday: 24,
} as const;

export interface WorkProfileLimits {
  maxWeeklyHours: number;
  maxWorkdaysPerWeek: number;
  maxHoursPerWorkday: number;
}

export function normalizeWorkProfileLimits(raw: {
  max_weekly_hours: number;
  max_workdays_per_week: number;
  max_hours_per_workday: number;
}): WorkProfileLimits {
  return {
    maxWeeklyHours: raw.max_weekly_hours,
    maxWorkdaysPerWeek: raw.max_workdays_per_week,
    maxHoursPerWorkday: raw.max_hours_per_workday,
  };
}

/**
 * Effective Stunden/Woche ceiling for a given Tage/Woche count:
 * min(maxWeeklyHours, workdays × maxHoursPerWorkday).
 */
export function effectiveMaxWeeklyHours(
  workdaysPerWeek: number,
  limits: Pick<WorkProfileLimits, "maxWeeklyHours" | "maxHoursPerWorkday">,
): number {
  if (!Number.isFinite(workdaysPerWeek) || workdaysPerWeek < 1) {
    return limits.maxWeeklyHours;
  }
  const fromWorkdays = workdaysPerWeek * limits.maxHoursPerWorkday;
  return Math.min(limits.maxWeeklyHours, fromWorkdays);
}

/** True when weekly hours exceed workdays × max hours per workday. */
export function weeklyHoursExceedWorkdayCap(
  weeklyHours: number,
  workdaysPerWeek: number,
  maxHoursPerWorkday: number,
): boolean {
  if (
    !Number.isFinite(weeklyHours) ||
    !Number.isFinite(workdaysPerWeek) ||
    !Number.isFinite(maxHoursPerWorkday) ||
    workdaysPerWeek < 1
  ) {
    return false;
  }
  return weeklyHours > workdaysPerWeek * maxHoursPerWorkday + 1e-9;
}

export function isWorkProfileWithinLimits(
  profile: { weeklyHours: number; workdaysPerWeek: number },
  limits: WorkProfileLimits,
): boolean {
  if (profile.weeklyHours > limits.maxWeeklyHours) return false;
  if (profile.workdaysPerWeek > limits.maxWorkdaysPerWeek) return false;
  if (
    weeklyHoursExceedWorkdayCap(
      profile.weeklyHours,
      profile.workdaysPerWeek,
      limits.maxHoursPerWorkday,
    )
  ) {
    return false;
  }
  return true;
}
